import { IEducationalVideoItem, IEducationalVideoItemAsJson } from '.';
import { enumEducationalVideoItemType, enumEducationalVideoItemTypeConvert } from '../../enums';

/** 
 * @class EducationalVideoItem An individual product video item
 */
export class EducationalVideoItem implements IEducationalVideoItem {
  /**
   * @method Constructor method
   * @param {string} id Id of the product video item
   * @param {enumEducationalVideoItemType} itemType The type of product video item (Catalog, InformationalVideo, TrainingVideo)
   * @param {string} title The title of the product video item
   * @param {string} description The description of the product video item
   * @param {string} displayUrl If applicable, a URL to display for the product video item 
   * @param {Array<string>} childIds If present, the Ids of child product video items for the product video item 
   */
  constructor(
    id: string,
    itemType: enumEducationalVideoItemType,
    title: string,
    description: string,
    displayUrl: string,
    childIds: Array<string>
  ) {
    this._id = id;
    this._itemType = itemType;
    this._title = title;
    this._description = description;
    this._displayUrl = displayUrl;
    this._childIds = childIds;
  }


  /*-----------------------------------------------*/
  /**
   * @property {string} _id Id of the product video item
   */
  private _id: string;

  /**
   * @method id Getter method for _id
   */
  get id(): string {
    return this._id;
  }

  /**
   * @method id Setter method for _id
   * @param {string} value The input value for setting _id
   */
  set id(value: string) {
    this._id = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {enumEducationalVideoItemType} _itemType The type of product video item (Catalog, InformationalVideo, TrainingVideo)
   */
  private _itemType: enumEducationalVideoItemType;

  /**
   * @method enumProduitemTypectVideoItemType Getter method for _itemType
   */
  get itemType(): enumEducationalVideoItemType {
    return this._itemType;
  }

  /**
   * @method itemType Setter method for _itemType
   * @param {enumEducationalVideoItemType} value The input value for setting _itemType
   */
  set itemType(value: enumEducationalVideoItemType) {
    this._itemType = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {string} _title The title of the product video item
   */
  private _title: string;

  /**
   * @method title Getter method for _title
   */
  get title(): string {
    return this._title;
  }

  /**
   * @method title Setter method for _title
   * @param {string} value The input value for setting _title
   */
  set title(value: string) {
    this._title = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {string} _description The description of the product video item
   */
  private _description: string;

  /**
   * @method description Getter method for _description
   */
  get description(): string {
    return this._description;
  }

  /**
   * @method description Setter method for _description
   * @param {string} value The input value for setting _description
   */
  set description(value: string) {
    this._description = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {string} _displayUrl If applicable, a URL to display for the product video item 
   */
  private _displayUrl: string;

  /**
   * @method displayUrl Getter method for _displayUrl
   */
  get displayUrl(): string {
    return this._displayUrl;
  }

  /**
   * @method displayUrl Setter method for _displayUrl
   * @param {string} value The input value for setting _displayUrl
   */
  set displayUrl(value: string) {
    this._displayUrl = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @property {Array<string>} _childIds If present, the Ids of child product video items for the product video item 
   */
  private _childIds: Array<string>;

  /**
   * @method Array<string> Getter method for _childIds
   */
  get childIds(): Array<string> {
    return this._childIds;
  }

  /**
   * @method Array<string> Setter method for _childIds
   * @param {Array<string>} value The input value for setting _childIds
   */
  set childIds(value: Array<string>) {
    this._childIds = value;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @method copy Performs a "deep copy" of the instance, which includes a copy of all contained objects.
   * @returns {IEducationalVideoItem} A "deep copy" of the object instance, including a "deep copy" of all contained objects.
   */
  copy(): IEducationalVideoItem {
    // use Object.create() to create a new instance, and then Object.assign() to assign all core properties
    let copyOfObject: IEducationalVideoItem = Object.create(EducationalVideoItem.prototype);
    Object.assign(copyOfObject, this);

    // there are no contained objects to copy

    return copyOfObject;
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @method toJSON Serializes an instance of this class to a JSON object, including contained
   * objects (if requested).
   * @param {boolean} includeContainedObjects A boolean flag indicating whether to include contained objects.
   * @returns A JSON object with serialized data from 'this' class instance.
   */
  toJSON(includeContainedObjects: boolean = true): IEducationalVideoItemAsJson {
    try {
      // create a json object and copy all field values to the json object 
      const jsonObject: IEducationalVideoItemAsJson = {
        id: this.id,
        itemType: this.itemType,
        title: this.title,
        description: this.description,
        displayUrl: this.displayUrl,
        childIds: [...this.childIds]
      }

      // if requested to include contained objects, serialize contained objects
      if (includeContainedObjects) {
        // this class is currently designed to have no contained objects
      }

      return jsonObject;

    } catch (error: any) {
      // TODO: log error
      // re-throw error
      throw error;
    }
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @method fromJSON Derializes an instance of this class from a JSON object, along with any contained 
   * objects (if requested).
   * @param {IEducationalVideoItemAsJson} jsonObject A JSON version of a class instance.
   * @param {boolean} includeContainedObjects A boolean flag indicating whether to include contained objects.
   * @returns An instance of 'this' class with values copied from the jsonObject
   */
  static fromJSON(jsonObject: IEducationalVideoItemAsJson, includeContainedObjects: boolean = true): IEducationalVideoItem {
    try {
      // create a new instance of this class
      let productVideoItemObject: EducationalVideoItem = Object.create(EducationalVideoItem.prototype);

      // copy any additional field values from the json object 
      if (jsonObject.id !== undefined) {
        productVideoItemObject.id = jsonObject.id;
      }

      if (jsonObject.itemType !== undefined) {
        productVideoItemObject.itemType = enumEducationalVideoItemTypeConvert.fromString(jsonObject.itemType);
      }

      if (jsonObject.title !== undefined) {
        productVideoItemObject.title = jsonObject.title;
      }

      if (jsonObject.description !== undefined) {
        productVideoItemObject.description = jsonObject.description;
      }

      if (jsonObject.displayUrl !== undefined) {
        productVideoItemObject.displayUrl = jsonObject.displayUrl;
      }

      if (jsonObject.childIds) {
        productVideoItemObject.childIds = [...jsonObject.childIds];
      }

      // if request is to include contained objects, copy additional fields
      if (includeContainedObjects) {
        // this class doesn't have any contained objects to add
      }

      return productVideoItemObject;

    } catch (error: any) {
      // TODO: log error
      // re-throw error
      throw error;
    }
  }
  /*-----------------------------------------------*/

}
